import { clsx } from "clsx";
import { type MouseEvent, type ReactElement, forwardRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { getCssColor } from "../../../app-utils/color.js";
import { type IPileupFlag } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { type RegionFlag } from "../../../graphql.js";
import { FLAG_HEIGHT } from "../../../settings/settings.js";
import { flagShapePileupParams } from "./_mod.js";
import type { IAppState } from "@/redux/reducer.ts";

export interface IFlagShapeProps {
  flag: IPileupFlag;
  height: number;
  onFlagClick?: (event: MouseEvent<SVGGElement>, flagPileup: IPileupFlag) => void;
  hideFlagSymbol?: boolean;
}

/**
 * A visualization component that draws the shape for a flag.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is pileup flag whose view coordinates and a width have already been computed elsewhere. The
 * FlagShape renders the flag symbol.
 *
 * Overview of shape dimensions:
 *
 * <g>
 * | (x,y) ¦             (midpoint)                    |
 * | <-------------------- width --------------------> |
 * | <-- midpointOffset --> ¦                          |
 * |                        | <- symbolWidth -> ¦      |
 * </g>
 *
 * created on 08/11/2017
 */
const FlagShape = forwardRef<SVGGElement, IFlagShapeProps>(function FlagShape(props, ref) {
  const useSpecificFlagsColoring = useSelector<IAppState, boolean>(
    (state) => state.auth.session.params.useSpecificFlagsColoring,
  );
  const handleFlagClick = useCallback(
    (event: MouseEvent<SVGGElement>) => {
      props.onFlagClick?.(event, props.flag);
    },
    [props.flag],
  );

  const pileupFlag = props.flag;
  const pennonHeight = 11;
  const symbolWidth = flagShapePileupParams.featureHeight;

  const color = useMemo(
    () => getFlagColor(pileupFlag.flag, useSpecificFlagsColoring),
    [props.flag, useSpecificFlagsColoring],
  );

  // Create actual shape elements
  const symbolElement = createSymbolElement(color, symbolWidth, pennonHeight, pileupFlag, props.hideFlagSymbol);

  return (
    <g key={`flag-symbol-${pileupFlag.flag.id}`} ref={ref} onClick={handleFlagClick}>
      {symbolElement}
    </g>
  );
});

const getFlagColor = (flag: RegionFlag, useSpecificFlagsColoring: boolean) => {
  if (useSpecificFlagsColoring) {
    return getCssColor(flag.color);
  }
  return getCssColor(flag.project.color);
};

const createSymbolElement = (
  color: string,
  symbolWidth: number,
  pennonHeight: number,
  pileupFlag: IPileupFlag,
  hideFlagSymbol: boolean,
) => {
  // points are located as:
  //
  //             2----------------3
  //             |               /
  //             |              4
  //             |               \
  //             |    6-----------5
  //             |    |
  //             |    |
  // 11----------1    7---------8
  //  |                         |
  // 10-------------------------9

  // caution: midpoint can be 0!
  const shouldDrawFlag: boolean = pileupFlag.midpoint !== null && !hideFlagSymbol;

  const fifthSymbolWidth: number = Math.round(symbolWidth / 5);
  const seventhSymbolWidth: number = Math.round(symbolWidth / 7);

  let rangeLeftX: number = Math.min(pileupFlag.x);
  let rangeRightX: number = Math.max(pileupFlag.x + pileupFlag.width);

  let midPoint: number;
  let poleLeftX: number;
  let poleRightX: number;

  if (shouldDrawFlag) {
    midPoint = pileupFlag.midpoint + 0.5;
    poleLeftX = midPoint - seventhSymbolWidth;
    poleRightX = midPoint + seventhSymbolWidth;

    // the range should never become narrower than the flag pole
    rangeLeftX = Math.min(rangeLeftX, poleLeftX);
    rangeRightX = Math.max(rangeRightX, poleRightX);
  }

  const rangeBarHeight = 4;
  const flagHeight: number = hideFlagSymbol ? 0 : FLAG_HEIGHT - rangeBarHeight;

  const flagStyleContent = { fill: color, cursor: "pointer" };
  const rangeOutlineStyleContent = { stroke: color, strokeWidth: 1 };

  // some x coordinates are used for the shape as well as wor a partial white outline
  const pnt1x: number = poleLeftX;
  const pnt2x: number = poleLeftX;
  const pnt3x: number = midPoint + symbolWidth;
  const pnt4x: number = midPoint + symbolWidth - fifthSymbolWidth;
  const pnt5x: number = midPoint + symbolWidth;
  const pnt8x: number = rangeRightX;
  const pnt9x: number = rangeRightX;
  const pnt10x: number = rangeLeftX;
  const pnt11x: number = rangeLeftX;

  const pnt1y: number = flagHeight;
  const pnt2y = 0;
  const pnt3y = 0;
  const pnt4y: number = Math.round(pennonHeight / 2);
  const pnt5y: number = pennonHeight - 0.5;
  const pnt8y: number = flagHeight;
  const pnt9y: number = flagHeight + rangeBarHeight;
  const pnt10y: number = flagHeight + rangeBarHeight;
  const pnt11y: number = flagHeight;

  // shape points
  const pnt1 = `${pnt1x}, ${pnt1y}`;
  const pnt2 = `${pnt2x}, ${pnt2y}`;
  const pnt3 = `${pnt3x}, ${pnt3y}`;
  const pnt4 = `${pnt4x}, ${pnt4y}`;
  const pnt5 = `${pnt5x}, ${pnt5y}`;
  const pnt6 = `${poleRightX},${pennonHeight - 0.5}`;
  const pnt7 = `${poleRightX},${flagHeight}`;
  const pnt8 = `${pnt8x},${pnt8y}`;
  const pnt9 = `${pnt9x},${pnt9y}`;
  const pnt10 = `${pnt10x},${pnt10y}`;
  const pnt11 = `${pnt11x},${pnt11y}`;

  let flagSymbol: ReactElement[] = [];
  const key = `flag-symbol-${pileupFlag.flag.id}`;
  // only draw the flag if it is visible
  if (shouldDrawFlag) {
    flagSymbol = [
      <polygon
        key={`${key}-p1`}
        style={flagStyleContent}
        points={`${pnt1} ${pnt2} ${pnt3} ${pnt4} ${pnt5} ${pnt6} ${pnt7}`}
      />,
    ];
  }

  const overallHeight: number = hideFlagSymbol ? rangeBarHeight + 2 : FLAG_HEIGHT;
  const yShift: number = (overallHeight + flagShapePileupParams.lanePadding) * pileupFlag.lane + 0.5;

  return (
    <g transform={`translate(0.5,${yShift})`} id={`flag-shape-${key}`} style={{ pointerEvents: "all" }}>
      <polygon style={flagStyleContent} opacity={0.5} points={`${pnt8} ${pnt9} ${pnt10} ${pnt11}`} />
      <line style={rangeOutlineStyleContent} x1={pnt11x} y1={pnt11y} x2={pnt8x} y2={pnt8y} />
      <line style={rangeOutlineStyleContent} x1={pnt9x} y1={pnt9y} x2={pnt10x} y2={pnt10y} />
      {!pileupFlag.startCut && (
        <line style={rangeOutlineStyleContent} x1={pnt10x} y1={pnt10y} x2={pnt11x} y2={pnt11y} />
      )}
      {!pileupFlag.endCut && <line style={rangeOutlineStyleContent} x1={pnt8x} y1={pnt8y} x2={pnt9x} y2={pnt9y} />}
      {flagSymbol}
    </g>
  );
};

export { FlagShape };
